import { useState, useEffect } from "react";

const MOBILE_BREAKPOINT = 768;

/**
 * Hook to detect if user prefers reduced motion
 * Returns true if prefers-reduced-motion is set
 */
export const useReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return prefersReducedMotion;
};

/**
 * Hook to detect if device is mobile (for performance optimizations)
 */
export const useIsMobileDevice = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

/**
 * Combined hook: returns true if we should reduce/skip animations
 * - User prefers reduced motion, OR
 * - Device is mobile (< 768px viewport)
 * 
 * Use this for performance-heavy animations like whileInView
 */
export const useShouldReduceMotion = () => {
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobileDevice();
  
  return prefersReducedMotion || isMobile;
};
